import { useLocation } from 'react-router-dom';
import { observer } from '@react';
import { useI18n } from '@react/hooks';
import { railStore } from '@/stores/RailStore';
import { buildBreadcrumbs } from '@/features/layout/logic';

function MenuIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <line x1="3" y1="12" x2="21" y2="12" />
      <line x1="3" y1="6" x2="21" y2="6" />
      <line x1="3" y1="18" x2="21" y2="18" />
    </svg>
  );
}

function PanelRightIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <line x1="15" y1="3" x2="15" y2="21" />
    </svg>
  );
}

function ChevronRightIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

interface NavbarProps {
  onMobileMenuOpen: () => void;
}

export const Navbar = observer(({ onMobileMenuOpen }: NavbarProps) => {
  const { t } = useI18n();
  const location = useLocation();
  const breadcrumbs = buildBreadcrumbs(location.pathname, t);

  return (
    <header className="sticky top-0 z-30 flex items-center h-12 px-4 bg-background/95 backdrop-blur border-b border-border">
      {/* Mobile hamburger */}
      <button
        onClick={onMobileMenuOpen}
        className="md:hidden mr-3 p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
        aria-label={t('layout.navbar.openMenu')}
      >
        <MenuIcon className="h-5 w-5" />
      </button>

      {/* Breadcrumb */}
      <nav aria-label={t('layout.navbar.breadcrumb')} className="flex items-center gap-1 text-sm min-w-0 flex-1">
        {breadcrumbs.map((crumb, i) => (
          <span key={crumb.href} className="flex items-center gap-1 min-w-0">
            {i > 0 && <ChevronRightIcon className="h-3 w-3 text-muted-foreground flex-shrink-0" />}
            {i === breadcrumbs.length - 1 ? (
              <span className="font-medium text-foreground truncate">{crumb.label}</span>
            ) : (
              <a href={crumb.href} className="text-muted-foreground hover:text-foreground truncate transition-colors">
                {crumb.label}
              </a>
            )}
          </span>
        ))}
      </nav>

      {/* Right actions */}
      <div className="flex items-center gap-1 ml-auto">
        {/* Rail toggle — absent on routes where the panel has nothing to show,
            rather than opening onto "select an item to see details". */}
        {railStore.hasContent && (
          <button
            onClick={() => railStore.toggle()}
            className={`p-1.5 rounded-md transition-colors ${
              railStore.isOpen
                ? 'bg-accent text-accent-foreground'
                : 'text-muted-foreground hover:text-foreground hover:bg-muted'
            }`}
            aria-label={t('layout.navbar.toggleRail')}
            aria-pressed={railStore.isOpen}
          >
            <PanelRightIcon className="h-4 w-4" />
          </button>
        )}
      </div>
    </header>
  );
});
